import React, { useCallback, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import ProfileForm from '../components/ProfileForm';
import { FaStar, FaCheckCircle, FaMapMarkerAlt } from 'react-icons/fa';
import './ProfilePage.css';

const SERVICE_LABELS = {
    'electrician': 'Electrician',
    'plumber': 'Plumber',
    'carpenter': 'Carpenter',
    'painter': 'Painter',
    'ac-repair': 'AC Repair',
    'cleaning': 'Cleaning',
    'glass-windows': 'Glass & Windows',
    'home-renovation': 'Home Renovation',
};

const ProfilePage = () => {
    const { currentUser } = useAuth();
    const [profile, setProfile] = useState(null);

    const loadProfile = useCallback(async () => {
        if (!currentUser) return;
        try {
            const snap = await getDoc(doc(db, 'serviceProviders', currentUser.uid));
            if (snap.exists()) setProfile(snap.data());
        } catch (e) {
            console.error('Failed to load profile:', e);
        }
    }, [currentUser]);

    useEffect(() => { loadProfile(); }, [loadProfile]);

    const displayName = profile?.name || currentUser?.displayName || 'Your Name';
    const initials = displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    const serviceLabel = SERVICE_LABELS[profile?.serviceCategory] || profile?.serviceCategory || 'Service Provider';
    const memberYear = profile?.createdAt ? new Date(profile.createdAt).getFullYear() : '—';

    return (
        <div className="profile-page">
            <div className="container">
                <div className="profile-grid">
                    <div className="profile-sidebar">
                        <div className="profile-card">
                            <div className="profile-avatar">
                                {initials}
                            </div>
                            <h2>{displayName}</h2>
                            <p className="profile-title">{serviceLabel}</p>

                            {profile?.location && (
                                <p className="profile-location">
                                    <FaMapMarkerAlt style={{ marginRight: 4 }} />
                                    {profile.location}
                                </p>
                            )}

                            <div className="profile-stats">
                                <div className="stat-item">
                                    <div className="stat-icon"><FaStar /></div>
                                    <div>
                                        <div className="stat-value">{profile?.rating ? profile.rating.toFixed(1) : '—'}</div>
                                        <div className="stat-label">Rating</div>
                                    </div>
                                </div>
                                <div className="stat-item">
                                    <div className="stat-icon"><FaCheckCircle /></div>
                                    <div>
                                        <div className="stat-value">{profile?.completedJobs ?? '—'}</div>
                                        <div className="stat-label">Jobs Done</div>
                                    </div>
                                </div>
                            </div>

                            <div className="profile-info">
                                <div className="info-item">
                                    <span className="info-label">Email:</span>
                                    <span>{currentUser?.email || '—'}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Phone:</span>
                                    <span>{profile?.phone || '—'}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Member Since:</span>
                                    <span>{memberYear}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Experience:</span>
                                    <span>{profile?.experience ? `${profile.experience} yrs` : '—'}</span>
                                </div>
                                {profile?.hourlyRate ? (
                                    <div className="info-item">
                                        <span className="info-label">Rate:</span>
                                        <span>₹{profile.hourlyRate}/hr</span>
                                    </div>
                                ) : null}
                            </div>

                            {profile?.isVerified && (
                                <div className="verified-tag"><FaCheckCircle /> Verified Professional</div>
                            )}
                        </div>

                        {profile?.bio && (
                            <div className="bio-section">
                                <h3>About</h3>
                                <p>{profile.bio}</p>
                            </div>
                        )}

                        {Array.isArray(profile?.skills) && profile.skills.length > 0 && (
                            <div className="skills-section">
                                <h3>Skills</h3>
                                <div className="skills-list">
                                    {profile.skills.map((skill, i) => (
                                        <span key={i} className="skill-tag">{skill}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="profile-main">
                        <ProfileForm onProfileSaved={loadProfile} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
